/* ruleEvaluate — in-browser dry-run for a typed Predicate AST.
 *
 * Spec 07 §"Builder data flow". The Builder's "Test on {project}"
 * button feeds a Predicate + the live project's GraphNode[] to this
 * function and renders the result. Designers see the rule fire on
 * their real data before they Promote.
 *
 * This wraps the existing scalar evaluator in `lib/ruleEngine.ts`
 * and adds the range / set predicates that ruleEngine deliberately
 * skipped. Aggregation + graph_pattern / exists / forbidden are
 * reported honestly as "evaluates server-side" — never silently
 * returning false (CLAUDE.md §5).
 */

import type { Predicate, ExtendedTerm, NodePattern } from "../types/rule";
import type { GraphNode, NodeKind } from "../types/ontology";

type Scalar = number | string | boolean | null;

function fieldOf(node: GraphNode, path: string): Scalar {
  const [kind, field] = path.split(".") as [NodeKind, string];
  if (node.kind !== kind) return null;
  if (field === undefined) return null;
  const v = node.attrs[field];
  return v === undefined ? null : v;
}

function termValue(node: GraphNode, t: ExtendedTerm): Scalar {
  if (t.kind === "lit")   return t.value;
  if (t.kind === "field") return fieldOf(node, t.path);
  return null; // agg — evaluated server-side
}

function inSet(v: Scalar, values: ReadonlyArray<string | number>): boolean {
  if (v === null) return false;
  return values.includes(v as string | number);
}
function inRange(v: Scalar, lo: number, hi: number): boolean {
  return typeof v === "number" && v >= lo && v <= hi;
}

function patternMatches(node: GraphNode, np: NodePattern): boolean {
  if (node.kind !== (np.nodeKind as NodeKind)) return false;
  if (np.filterAttr !== undefined && np.filterValue !== undefined) {
    const v = node.attrs[np.filterAttr];
    return v === np.filterValue;
  }
  return true;
}

export type UnevaluableKind = "agg" | "graph_pattern" | "exists" | "forbidden" | "spatial";

interface EvalCtx {
  /** Predicate kinds we deferred to server-side evaluation. */
  unevaluable: Set<UnevaluableKind>;
}

function evalNode(p: Predicate, node: GraphNode, ctx: EvalCtx): boolean {
  switch (p.kind) {
    case "cmp": {
      if (p.lhs.kind === "agg" || p.rhs.kind === "agg") { ctx.unevaluable.add("agg"); return false; }
      const l = termValue(node, p.lhs);
      const r = termValue(node, p.rhs);
      if (l === null || r === null) return false;
      if (typeof l === "number" && typeof r === "number") {
        switch (p.op) {
          case ">":  return l > r;
          case ">=": return l >= r;
          case "<":  return l < r;
          case "<=": return l <= r;
          case "==": return l === r;
          case "!=": return l !== r;
        }
      }
      if (p.op === "==") return l === r;
      if (p.op === "!=") return l !== r;
      return false;
    }
    case "and": return evalNode(p.left, node, ctx) && evalNode(p.right, node, ctx);
    case "or":  return evalNode(p.left, node, ctx) || evalNode(p.right, node, ctx);
    case "not": return !evalNode(p.inner, node, ctx);
    case "in_range": {
      if (p.value.kind === "agg") { ctx.unevaluable.add("agg"); return false; }
      return inRange(termValue(node, p.value), p.range.lo, p.range.hi);
    }
    case "not_in_range": {
      if (p.value.kind === "agg") { ctx.unevaluable.add("agg"); return false; }
      return !inRange(termValue(node, p.value), p.range.lo, p.range.hi);
    }
    case "in_set": {
      if (p.value.kind === "agg") { ctx.unevaluable.add("agg"); return false; }
      return inSet(termValue(node, p.value), p.set.values);
    }
    case "not_in_set": {
      if (p.value.kind === "agg") { ctx.unevaluable.add("agg"); return false; }
      return !inSet(termValue(node, p.value), p.set.values);
    }
    case "graph_pattern": {
      // Local-only weak match: subject must match this node; we cannot
      // join against the full graph here without breaking the per-node
      // loop. Defer to server-side and report it honestly.
      ctx.unevaluable.add("graph_pattern");
      return patternMatches(node, p.subject);
    }
    case "exists":   ctx.unevaluable.add("exists");    return false;
    case "forbidden": ctx.unevaluable.add("forbidden"); return false;
    // Spatial predicates evaluate across the whole graph (Seam 6 via
    // lib/spatialPredicate.ts), not per-node. evalNode reports them as
    // unevaluable in this loop; the dry-run caller invokes the spatial
    // evaluator alongside and merges the verdicts.
    case "clearance":
    case "sightline":
    case "work_triangle":
    case "egress_path":
      ctx.unevaluable.add("spatial");
      return false;
  }
}

export interface DryRunResult {
  /** Node ids that fire the rule under the locally evaluable parts of the AST. */
  matched: string[];
  /** Predicate kinds the browser could not evaluate — they ride on the agent. */
  unevaluable: UnevaluableKind[];
  /** True if the AST contains *only* locally-evaluable predicates. */
  fullyEvaluable: boolean;
}

export function dryRun(p: Predicate, nodes: ReadonlyArray<GraphNode>): DryRunResult {
  const ctx: EvalCtx = { unevaluable: new Set() };
  const matched: string[] = [];
  for (const n of nodes) {
    if (evalNode(p, n, ctx)) matched.push(n.id);
  }
  return {
    matched,
    unevaluable: Array.from(ctx.unevaluable),
    fullyEvaluable: ctx.unevaluable.size === 0,
  };
}
